import { Indicator } from '#~/control/calibration/components/indicator/index.js';
import { Status } from '#~/device/components/status/index.js';
import { toStatus } from '#~/device/index.js';
import { useTemplate } from '#~/device/registry/hooks.js';
// Transitory-placeholder slice (W2, spec §2.1). When the id is a
// placeholder (absent from `room.devices`), synthesize the device-object
// so the tile renders and the chip click deep-links via the synthetic
// device (spec §3.2/§3.4 — `useSettings` takes the device by argument).
import { toSyntheticDevice, useTransitoryDevice } from '#~/device/transitory.js';
import { useRoom } from '#~/room/hooks.js';
import { chipClickOptions } from '#~/room/components/video/sources/item/chip-click.js';
import { NeedsAttentionChip } from '#~/room/components/video/sources/item/needs-attention-chip.jsx';
import {
  shouldShowChip,
  shouldShowStatusBadge
} from '#~/room/components/video/sources/item/suppression.js';
import { useChipState } from '#~/room/components/video/sources/item/use-chip-state.js';
import { useTileNeedsAttentionRingClass } from '#~/room/components/video/sources/item/tile-needs-attention-ring.jsx';
import type { AnnotationProps } from './types.js';
import { Ellipses } from '@studio/design-system/label';
import { isCalibrated, isGenericCalibration } from '@studio/models/control/calibration';
import { StatusPowerState } from '@tryiris-ai/common.protojs/agent/v1/agent';
import { isNotNil } from '@tryiris-ai/js-types';
import type { T_Nullable } from '@tryiris-ai/js-types';
import { dequal } from 'dequal/lite';
import type { ReactElement } from 'react';
import { useMemo } from 'react';
import { twMerge } from 'cn';

/**
 * `<Annotation>` component — overlays on each tile in the multi-view
 * main grid (`Content` in this same folder; rendered only when
 * `arrangement !== Single`).
 *
 * Per Figma reference (Multi-Add Phase 1 manual QA): the per-tile chip
 * ("Authenticate" / "Connect controls" / etc.) surfaces on every tile
 * of the multi-view grid so users can deep-link to the device's
 * Authentication / Driver tab from the main view. Mirrors the rules
 * from `item.tsx`:
 *   - chipState comes from `deriveChipState` (pure helper).
 *   - Chip occupies top-right; the calibration indicator displaces to
 *     top-left when chipState !== null so the two don't collide.
 *   - `<Status>` badge is suppressed when chipState !== null (the chip
 *     carries the verdict the badge would otherwise render redundantly).
 *   - Click target invokes `useSettings(device, chipClickOptions(...))`
 *     so the editor opens at the targeted sub-tab — same deep-link
 *     contract the sidebar tiles use.
 */
export const Annotation = ({
  className,
  deviceId,
  openSettings,
  ...props
}: AnnotationProps): T_Nullable<ReactElement> => {
  // Read the full device record (not just name+status) so we can pass it
  // to `settings()` when the chip is clicked — same input shape the
  // sidebar `<Item>` uses for its chip click.
  const realDevice = useRoom((µ) => µ.devices[deviceId], dequal);
  // Placeholder fallback (spec §3.2): a transitory id is never in
  // `room.devices` (agent authority, §1.2). Synthesize the device-object
  // from the slice so render + click work off the same shape. The
  // synthetic device carries `video` (passes `isVideoDevice`) and
  // `transitoryKind` (drives the "Authenticate" chip).
  const placeholder = useTransitoryDevice(deviceId);
  const device = realDevice ?? (placeholder ? toSyntheticDevice(placeholder) : undefined);

  // Hook calls must run unconditionally on every render — these stay at
  // the top, before the null-guard short-circuit. NOTE: the settings-dialog
  // opener is NOT created here (that would tie the dialog's lifetime to this
  // ephemeral tile); it is owned by the stable <Content> parent and passed in
  // as `openSettings` — see content/types.ts.
  const status = useMemo(() => (device ? toStatus(device) : undefined), [device]);
  // Shared chip-state derivation. `useChipState` wraps the pure
  // `deriveChipState` helper with the proxy + device reads, so both
  // the sidebar `<Item>` and this main-grid overlay end up calling
  // the same logic from a single place.
  const chipState = useChipState(deviceId);
  // Tile-level yellow warning outline — per Figma 2026-05-14 the
  // main-canvas variant is RECT-CORNERED (no `rounded-4`), while the
  // sidebar `<Item>` opts into the rounded variant. Both surfaces
  // light up with the same 1px inset outline-warning band when
  // chipState !== null.
  const ringClass = useTileNeedsAttentionRingClass(chipState);
  // ENG-4495 C2: resolve the registry template for the device's model.
  // Called unconditionally (before the early return) to keep hook order
  // stable; an undefined modelId yields an undefined template.
  const template = useTemplate(device?.modelId);

  if (!device || !status) {
    return null;
  }

  const { name } = device;
  const calibration = status.control?.calibration?.status;
  // ENG-4495 D3: tri-state, permissive on absence — only a LOADED generic ===
  // true suppresses the calibration icon. An unresolved template (undefined)
  // behaves exactly like today, never `?? false`.
  const isGeneric = template?.model?.generic === true;
  // Same gate as the sidebar Item's indicator: calibration drives PTZ tracking, so a camera
  // without absolute pan AND tilt is never told it needs it. Per rule:
  // absent-data-gates-permissively (ai.knowledge/global) — an unreported capability never nags.
  const capabilities = device.control?.capabilities;
  const canCalibrate = !!capabilities?.pan?.absolute && !!capabilities?.tilt?.absolute;

  const handleChipClick = (): void => {
    openSettings(device, chipClickOptions(chipState));
  };

  // shouldShowChip already factors in collapsed mode; the multi-view
  // grid is by definition NOT collapsed (it's the expanded tile path),
  // so we pass `false`. Defensive coverage in case the helper's contract
  // shifts in the future.
  const renderChip = shouldShowChip(chipState, false) && chipState !== null;

  return (
    // Wrapper stacking contract — load-bearing for chip clickability.
    // When a control tool (Center, FastFrame, Zoom, Tracking) is
    // selected, `<Content>` renders `<Interaction>` after `<Annotation>`
    // as a sibling inside the same per-tile `motion.div`. Inside
    // `<Interaction>` the tool's pointer-canvas handler is positioned
    // at `z-30` (see `interaction.tsx` — both the AutoTrackingHandler
    // branch and the generic `Element` branch hard-code `z-30`). To
    // keep the chip clickable, this wrapper must sit ABOVE that
    // `z-30` layer — `z-40` reserves headroom below the viewport-wide
    // joystick overlay (`z-50` in `joystick.tsx`). `pointer-events-none`
    // lets clicks in the wrapper's empty region fall through to the
    // tool handler underneath; interactive + hoverable descendants
    // (chip, passive name label, Status badge, calibration Indicator)
    // opt back in via `pointer-events-auto` so they still receive
    // clicks and hover. A previous attempt set this to `z-20`,
    // which competed below the `z-30` handler and left the chip
    // un-clickable when a tool was active.
    <div
      className={twMerge('pointer-events-none absolute inset-0 z-40', ringClass, className)}
      data-testid='annotation-wrapper'
      {...props}
    >
      {!!name && renderChip && (
        <NeedsAttentionChip
          className='pointer-events-auto absolute right-8 top-8 z-10'
          device={{ name }}
          onClick={handleChipClick}
          state={chipState}
        />
      )}

      {!!name && !renderChip && (
        <div
          className={twMerge(
            'pointer-events-auto absolute right-8 top-8 z-10 max-w-[150px] select-none overflow-hidden rounded bg-base-100/90 px-8 py-4 text-[12px] font-medium text-white'
          )}
        >
          <Ellipses title={name}>{name}</Ellipses>
        </div>
      )}

      {/*
        Suppress the <Status> badge when chipState !== null — the chip
        carries the per-aspect verdict that <Status> would otherwise
        render redundantly. Same suppression rule used in sidebar <Item>.
      */}
      {shouldShowStatusBadge(chipState) && (
        <Status className='pointer-events-auto' deviceId={deviceId} status={status} />
      )}

      {isNotNil(status.control) &&
        status.device?.agentStatus === 'online' &&
        status.device.powerState === StatusPowerState.POWER_STATE_ON &&
        ((!isCalibrated(calibration) && canCalibrate) || isGenericCalibration(calibration)) &&
        // ENG-4495 C2: a driverless (generic) model can never be calibrated —
        // never nag with the calibration icon.
        !isGeneric && (
          <Indicator
            className={twMerge(
              'pointer-events-auto absolute top-8',
              // Calibration indicator stays on the LEFT now that the
              // name+chip surface is permanently RIGHT-justified — no
              // more displacement logic needed (the old `calibrationOnLeft`
              // branch only existed to move the indicator out of the
              // chip's way; the name occupied the left corner instead).
              'left-8'
            )}
          />
        )}
    </div>
  );
};
